import * as THREE from "three";
import { walkingWith } from "../../sim/backpack";
import { BAIL_TIME, type Skater } from "../../skater";
import type { FlipAnim, GrindStyle } from "../../tricks";
import { setArm } from "../arm";
import { MAX_HIP, MIN_HIP } from "../ik";
import { clamp } from "../pose";
import type { SkaterLook, SkaterRig } from "../skater-parts";
import { BIKE_GRIP, BIKE_HEAD_Z, BIKE_PEDAL, BIKE_PEG_X, BIKE_REAR_HUB, BIKE_WHEEL_R, buildBike, pedalOffset, type Bike } from "./bike";
import { easeBy, footTo, gripWith, Held, heldGrab, RidePose, type RideCue, type RideRig } from "./ride-pose";

// The rider on the BMX (#141), posed over the board's pose each frame by
// `SkaterModel`. The bike's meshes are in bike.ts; getting on and off, the
// arm rate limit and the hand on the grips are the rides' shared `RidePose`.

export class BikePose implements RideRig {
  /** The BMX, built the first time someone gets on one; and how far its wheels have turned. */
  private bike: Bike | null = null;
  private wheelTurn = 0;
  /** How far the cranks have turned forward from rest (`pedalOffset`), and how far down on the seat the rider is (0..1, eased). */
  private crankTurn = 0;
  private bikeSeat = 0;
  /** Getting on and off, the arm rate limit and the walking hand (`ride-pose.ts`). */
  private readonly ride: RidePose;
  /** The bike's pitch and offset (manual, grind, peg stall), eased; the grab's amount and name, eased; hands on the grips. */
  private bikeCrouch = 0;
  private bikePitch = 0;
  private bikeYaw = 0;
  private bikeRoll = 0;
  private bikeLeanIn = 0;
  private bikeOff = new THREE.Vector3();
  /** The grab (or held manual trick) the hands are in, eased; switching lets go first. */
  private grab = new Held<string>(18);
  private bikeGrip = 1;
  private bikeStretch = 0;
  /** Walked beside the rider this frame (`walkBeside`), so `apply` leaves the bike where that put it. */
  private walked = false;

  constructor(
    private rig: SkaterRig,
    private look: SkaterLook,
  ) {
    this.ride = new RidePose(rig);
  }

  /**
   * On the BMX (#141): the board's hidden and the rider stands on the pedals
   * facing the bars, hands on the grips. Run after the board pose, it sets
   * the rider over it: the bike pitches up onto one wheel for a manual (and
   * the rider with it), hangs a peg on the rail in a grind and stands up on
   * a peg on the coping; flips whip the frame or spin the bars about the
   * head tube; grabs take a hand or a foot off; a bail throws the rider
   * over the bars. On foot the bike's away in the pack. Returns whether
   * it posed the rider (so the model re-reads the arms' raise).
   */
  apply(sk: Skater, dt: number, cue: Readonly<RideCue>): boolean {
    const on = sk.ride === "bmx" && sk.mode !== "foot";
    // Getting on or off blends the rider between the board's (or walking) pose and the bike's.
    const onW = this.ride.ease(on, dt);
    // Walked beside you on foot (`walkBeside`), it stays where that put it.
    if (this.bike) this.bike.root.visible = on || this.walked;
    if (onW === 0) return false;
    const bike = this.ensureBike();
    bike.root.visible = on || this.walked;
    if (on) this.rig.board.visible = false;
    this.ride.saveJoints();
    const R = BIKE_WHEEL_R;
    if (sk.mode === "ground") this.wheelTurn += (sk.speed * dt) / R;
    for (const w of bike.wheels) w.rotation.x = this.wheelTurn;
    // Rolling along sat down, the rider pedals (#138): the cranks turn with
    // the back wheel through the gearing, up to a cadence a rider keeps up
    // (faster than that, or rolling backwards, they coast), and only as far
    // as they're sat down (`bikeSeat`, last frame's), so stood up off a
    // landing or a crouch they ease in. Off the ground or in a manual they
    // come back round to rest, where the tricks' poses expect the feet,
    // no faster than the legs can follow.
    const pedalling = sk.mode === "ground" && !sk.manual;
    if (pedalling) {
      const cadence = Math.min(Math.max(0, sk.speed) / R / BIKE_GEAR, MAX_CADENCE) * this.bikeSeat;
      this.crankTurn = (this.crankTurn + cadence * dt) % (Math.PI * 2);
    } else {
      const rest = Math.round(this.crankTurn / (Math.PI * 2)) * Math.PI * 2;
      this.crankTurn += clamp(rest - this.crankTurn, -CRANK_REST_RATE * dt, CRANK_REST_RATE * dt);
    }
    bike.cranks.rotation.x = this.crankTurn;
    for (const p of bike.pedals) p.rotation.x = -this.crankTurn;
    bike.frame.rotation.set(0, 0, 0);
    bike.front.rotation.set(0, 0, 0);

    // Grabs ease in and out; switching grabs lets go of the last one first. A
    // held manual trick or a two-part grab (#138) is posed as the grabs it's made of (`BIKE_PARTS`).
    // A grab in a body flip (a Superman Backflip, #138) lets go in time for the hands to be back on the bars as it comes round.
    const grab = heldGrab(sk) ?? (sk.grab?.timed ? null : (sk.manual?.hold?.trick.name ?? null));
    this.grab.update(grab, dt);

    // Pitch about a hub: up on the back wheel for a manual, the front one for a
    // nose manual, stood up on a peg on the coping (the back one for a
    // Tooth-Pick, the front for a Nose-Pick). In a grind the bike hangs off
    // one side of the rail on the grind's pegs (`PEG_POSES`, #138): pitched
    // up off one peg, turned so a wheel crosses over the rail, or rolled
    // onto the crank.
    let pitch = 0;
    let yaw = 0;
    let roll = 0;
    let rearPivot = cue.manualTip < 0;
    const offTarget = TMP_V.set(0, 0.12 * cue.airTuck, 0);
    if (Math.abs(cue.manualTip) > 0.01) pitch = 0.42 * cue.manualTip + 0.06 * cue.manualWobble * Math.abs(cue.manualTip);
    if (sk.mode === "grind" && sk.grind) {
      const peg = PEG_POSES[sk.grind.trick.style] ?? PEG_POSES.fiftyfifty!;
      pitch = peg.pitch;
      yaw = peg.yaw;
      roll = peg.roll;
      rearPivot = peg.pivot === "rear";
      offTarget.set(-BIKE_PEG_X + peg.x, -R + 0.02 + peg.y, peg.z);
    } else if (sk.mode === "lip" && sk.lip) {
      const nose = sk.lip.trick.style === "nose";
      pitch = nose ? 1.1 : -1.1;
      rearPivot = !nose;
      offTarget.set(0, -R, nose ? -BIKE_HEAD_Z : -(BIKE_HEAD_Z + BIKE_REAR_HUB));
    }
    this.bikePitch += (pitch - this.bikePitch) * easeBy(16, dt);
    this.bikeYaw += (yaw - this.bikeYaw) * easeBy(16, dt);
    this.bikeRoll += (roll - this.bikeRoll) * easeBy(16, dt);
    this.bikeOff.lerp(offTarget, easeBy(16, dt));
    const pivotZ = rearPivot ? BIKE_HEAD_Z + BIKE_REAR_HUB : BIKE_HEAD_Z;
    const q = BIKE_Q.setFromAxisAngle(X_AXIS, this.bikePitch);
    if (this.bikeYaw !== 0 || this.bikeRoll !== 0) {
      q.premultiply(TMP_Q4.setFromAxisAngle(Z_AXIS, this.bikeRoll)).premultiply(TMP_Q3.setFromAxisAngle(Y_AXIS, this.bikeYaw));
    }
    // A Tabletop (#138) lays the bike flat out to one side, rider and all,
    // rolled about a point level with the grips so the hands and feet stay put on it.
    const table = this.part("Tabletop");
    const tableShift = TMP_V2.set(0, 0, 0);
    if (table > 0) {
      tableShift.copy(TABLETOP_AXIS_AT).applyQuaternion(q);
      q.multiply(TMP_Q4.setFromAxisAngle(Z_AXIS, -1.05 * table));
      tableShift.sub(TMP_V.copy(TABLETOP_AXIS_AT).applyQuaternion(q));
    }
    const pivot = BIKE_PIVOT.set(0, R, pivotZ);
    const base = BIKE_BASE.copy(pivot).sub(TMP_V.copy(pivot).applyQuaternion(q)).add(this.bikeOff).add(tableShift);
    if (!this.walked) {
      bike.root.quaternion.copy(q);
      bike.root.position.copy(base);
      this.ride.mountFrom(bike.root);
    } else this.ride.dismountTo(bike.root);

    // The rider: feet on the pedals, facing the bars (+z), leaning into turns.
    const riderQ = TMP_Q3.setFromAxisAngle(Y_AXIS, Math.PI / 2);
    riderQ.premultiply(TMP_Q4.setFromAxisAngle(Z_AXIS, -0.8 * cue.lean));
    riderQ.premultiply(q);
    const at = TMP_V2.set(0, BIKE_PEDAL.y + 0.02, BIKE_PEDAL.z);
    this.bikeCrouch += (sk.crouch - this.bikeCrouch) * easeBy(12, dt);
    const crouch = this.bikeCrouch;
    // Cruising, the rider sits back on the seat to pedal (#138); crouching to
    // build speed or to pop stands them up off it, and so does everything else.
    this.bikeSeat += ((pedalling ? 1 - crouch : 0) - this.bikeSeat) * easeBy(6, dt);
    const seated = this.bikeSeat;
    at.z -= SEAT_BACK * seated;
    // The low pedal's leg is near straight, where a little hip drop bends the
    // knee a lot: take a share of a landing's dip, so a big vert landing (a
    // Flair, #138) sinks without the knee snapping in one frame.
    let hipY = clamp(0.66 - 0.2 * crouch - 0.1 * cue.airTuck + BIKE_LAND_DIP * cue.landDip, MIN_HIP, MAX_HIP);
    hipY += (SEAT_HIP - hipY) * seated;
    const superman = this.part("Superman");
    // The legs straighten out behind at a steady rate, not eased: near
    // straight the knee turns a lot for a little hip, and from a crouched air
    // the eased start snapped it straight in a frame (#138).
    const stretchStep = BIKE_STRETCH_RATE * dt;
    this.bikeStretch += clamp(superman - this.bikeStretch, -stretchStep, stretchStep);
    if (superman > 0) {
      // Feet off the back, body stretched out flat behind the bars; further
      // back and lower over the bike with a hand on the seat (#138), so it reaches.
      riderQ.premultiply(TMP_Q4.setFromAxisAngle(X_AXIS, 0.9 * superman));
      const seat = this.part("Seat Grab");
      at.z -= (0.25 + 0.4 * seat) * superman;
      at.y += (0.3 - 0.25 * seat) * superman;
    }
    hipY += (MAX_HIP - hipY) * this.bikeStretch;
    if (sk.mode === "bail") {
      // Over the bars: the rider dives forward and down, the bike flips back behind.
      const fall = Math.min(1, sk.bailT / 0.3);
      const getUp = Math.max(0, (sk.bailT - (BAIL_TIME - 0.35)) / 0.35);
      const p = fall * (1 - getUp);
      riderQ.premultiply(TMP_Q4.setFromAxisAngle(X_AXIS, 1.5 * p));
      at.z += 0.9 * p;
      at.y -= 0.15 * p;
      const t = sk.bailT;
      if (t < BAIL_TIME - 0.3) {
        bike.root.rotation.x -= Math.min(Math.PI, t * 5);
        bike.root.position.z -= Math.min(1.2, t * 2.5);
        bike.root.position.y += Math.sin(Math.min(t * 4, Math.PI)) * 0.4;
      }
    }
    this.rig.rider.quaternion.copy(riderQ);
    this.rig.rider.position.copy(at.applyQuaternion(q)).add(base);
    this.rig.hips.position.set(0, hipY, 0);
    // Up on the back wheel the bars rise away from you: lean in after them so
    // the arms stay bent (at full stretch the reach flips the elbows, #138).
    // Eased on its own, slower than the tip, as the elbows are touchy near straight.
    this.bikeLeanIn += (0.2 * Math.max(0, -cue.manualTip) - this.bikeLeanIn) * easeBy(5, dt);
    // An X-Up (#138) turns the bars across you: lean down over them and turn
    // the shoulders after them so both hands still reach the grips. A
    // turndown turns them the same way, down and back: lean after it as far.
    const pop = sk.manual?.pop;
    const move = sk.flip
      ? { anim: sk.flip.trick.anim, n: sk.flip.trick.turns, p: Math.min(1, sk.flip.t / sk.flip.duration) }
      : pop
        ? { anim: pop.trick.anim, n: pop.trick.rotations, p: Math.min(1, pop.t / pop.duration) }
        : null;
    const turndown = move?.anim === "impossible" ? turndownAngle(move.p) : 0;
    const xUp = this.part("X-Up");
    const across = Math.max(xUp, turndown / X_UP_TURN);
    this.rig.torso.rotation.set(0, 0.2 * across, 0.5 + 0.35 * crouch + SEAT_LEAN * seated + this.bikeLeanIn + 0.65 * across, "YZX");
    this.rig.head.rotation.set(0, 0, -0.3 - 0.25 * crouch, "ZYX");
    // The right foot (+x, legs[0]) on the right pedal, the left on the left one, both in over the
    // cranks (at rest the right one's high); sat back on the seat, the pedals are out ahead.
    for (let i = 0; i < this.rig.legs.length; i++) {
      const leg = this.rig.legs[i]!;
      const pedal = pedalOffset(i === 0 ? 1 : -1, this.crankTurn, TMP_V);
      footTo(leg, hipY - pedal.y, pedal.z + SEAT_BACK * seated, -leg.z * 0.38);
    }
    const noFooter = this.part("No-Footer");
    this.rig.legs[1]!.hip.rotation.x -= 0.9 * Math.max(this.part("One-Footer"), noFooter);
    this.rig.legs[0]!.hip.rotation.x += 0.9 * noFooter - 1.1 * this.part("Can-Can");
    // A Nac-Nac (#138) swings the left leg back over the back wheel and out the far side.
    const nac = this.part("Nac-Nac");
    if (nac > 0) {
      this.rig.legs[1]!.hip.rotation.x += 1.2 * nac;
      this.rig.legs[1]!.hip.rotation.z += 0.9 * nac;
      this.rig.legs[1]!.knee.rotation.z += 0.5 * nac;
    }

    // Flips: whip the frame round, spin the bars, or turn them down and back.
    const moveAnim = move?.anim ?? null;
    // The bars turned across for an X-Up or a turndown go on before the hands
    // reach for the grips, so the hands go round with them.
    bike.front.rotation.y += X_UP_TURN * xUp + turndown;

    // Hands on the grips, eased off them for a No-Hander (arms out wide), while the bars spin round
    // (a barspin: you throw them and catch them), or for a bail (the flailing arms).
    const spinsBars = moveAnim !== null && SPINS_BARS.has(moveAnim);
    const gripping = sk.mode !== "bail" && !spinsBars && !(grab !== null && grab === this.grab.name && hasPart(grab, "No-Hander"));
    this.bikeGrip += ((gripping ? 1 : 0) - this.bikeGrip) * easeBy(8, dt);
    if (sk.mode !== "bail") {
      setArm(this.rig.armFront, -1, ARMS_OUT);
      setArm(this.rig.armBack, 1, ARMS_OUT);
    }
    if (this.bikeGrip > 0.001) {
      this.rig.root.updateMatrixWorld(true);
      for (let a = 0; a < 2; a++) {
        const arm = a === 0 ? this.rig.armFront : this.rig.armBack;
        const side = a === 0 ? 1 : -1;
        const target = bike.front.localToWorld(TMP_V.set(side * BIKE_GRIP.x, BIKE_GRIP.y, BIKE_GRIP.z));
        // A seat grab (#138) takes the right hand off the bars to the seat.
        const seat = side > 0 ? this.part("Seat Grab") : 0;
        if (seat > 0) target.lerp(bike.frame.localToWorld(TMP_V2.copy(BIKE_SEAT_GRAB)), seat);
        gripWith(arm, target, this.bikeGrip, true);
      }
    }
    this.ride.limitArms(sk.mode !== "bail" && onW === 1, dt);
    // The move goes on after the hands have found the grips: they let go to spin the bars and catch them after.
    if (move) whipBike(bike, move.anim, move.n, move.p);
    this.ride.blendJoints();
    return true;
  }

  /**
   * On foot with the BMX out of the pack (#138, #145): walked beside the
   * rider on the front arm's side, upright and leant in a little toward
   * them, the front hand on the near grip and the wheels rolling with the
   * walk. `out` (0..1, eased by the model) blends the arm to the grip from
   * wherever the walk left it. Run before `apply`, so getting on blends the
   * arm from where it held the grip, and stepping off leaves the bike beside
   * you while the rider blends off it.
   * Returns whether it posed the arm.
   */
  walkBeside(sk: Skater, dt: number, out: number): boolean {
    this.walked = false;
    if (out <= 0) return false;
    const bike = this.ensureBike();
    // The rider's own z is the model's x on foot (the walk turns it a quarter to face forward).
    const side = Math.sign(this.rig.armFront.shoulder.position.z) || 1;
    if (walkingWith(sk) === "bmx") {
      this.walked = true;
      bike.root.visible = true;
      bike.root.position.set(side * (WALK_HAND_OUT + BIKE_GRIP.x), 0, WALK_BIKE_Z);
      bike.root.rotation.set(0, 0, side * WALK_LEAN_IN);
      this.ride.noteBeside(bike.root);
      bike.frame.rotation.set(0, 0, 0);
      bike.front.rotation.set(0, 0, 0);
      this.wheelTurn += ((sk.foot?.speed ?? 0) * dt) / BIKE_WHEEL_R;
      for (const w of bike.wheels) w.rotation.x = this.wheelTurn;
    }
    if (!this.walked) {
      // Just got on: the arm stays where the last step walking left it, and
      // `apply` blends it from there onto the bars at its own pace. Put back
      // in the pack on foot, the hand eases back from the bars.
      // Getting on something else, the hand just lets go (the other ride's pose takes the arm).
      if (sk.mode !== "foot" && sk.ride !== "bmx") return false;
      this.ride.holdBeside(null, sk.mode === "foot" ? out : 1);
      return true;
    }
    this.rig.root.updateMatrixWorld(true);
    this.ride.holdBeside(bike.front.localToWorld(TMP_V.set(-side * BIKE_GRIP.x, BIKE_GRIP.y, BIKE_GRIP.z)), out);
    return true;
  }

  /** The BMX, built the first time it's needed and kept on the rider's turning group. */
  private ensureBike(): Bike {
    if (!this.bike) {
      this.bike = buildBike(this.look);
      this.rig.yawGroup.add(this.bike.root);
    }
    return this.bike;
  }

  /** How much of the grab `part` the rider is showing (0..1, eased): any grab or held trick made of it (`BIKE_PARTS`). */
  private part(part: BikePart): number {
    return this.grab.name !== null && hasPart(this.grab.name, part) ? this.grab.value : 0;
  }

}

/**
 * The bike's moves for the flip ids the ride's tricks use (`sim/rides.ts`),
 * `p` 0..1 of the way: kickflip a tailwhip (the frame spins about the head
 * tube), varial the other way, heelflip a barspin, impossible a turndown,
 * and the diagonals two at once.
 */
function whipBike(bike: Bike, anim: FlipAnim, n: number, p: number): void {
  const turn = Math.PI * 2 * n * p;
  switch (anim) {
    case "kickflip":
      bike.frame.rotation.y += turn;
      break;
    case "varial":
      bike.frame.rotation.y -= turn;
      break;
    case "heelflip":
      bike.front.rotation.y += turn;
      break;
    case "impossible":
      // The turndown went on before the hands reached for the grips (`apply`).
      break;
    case "treflip":
      bike.frame.rotation.y += turn;
      bike.front.rotation.y += turn;
      break;
    case "hardflip":
      bike.front.rotation.y += turn + turndownAngle(p);
      break;
    case "varialheel":
      bike.frame.rotation.y -= turn;
      bike.front.rotation.y += turn;
      break;
    default:
      bike.frame.rotation.y += turn;
  }
}

/**
 * How the bike sits on the rail in each peg grind (#138), keyed by the grind
 * style the ride's table gives it (`sim/rides.ts`). The rail runs along the
 * bike's right (+x) side, under the pegs. `pitch` turns the bike about the
 * `pivot` hub (negative lifts the front), `yaw` turns the front wheel over
 * the rail (+) or in toward you (-), `roll` tips it toward the rail, and
 * `x`, `y`, `z` shift it from where a Double Peg hangs.
 */
interface PegPose {
  pivot: "rear" | "front";
  pitch: number;
  yaw: number;
  roll: number;
  x: number;
  y: number;
  z: number;
}

const PEG_POSES: Readonly<Partial<Record<GrindStyle, PegPose>>> = {
  // Both pegs on the rail, the bike level beside it.
  fiftyfifty: { pivot: "rear", pitch: 0, yaw: 0, roll: 0, x: 0, y: 0, z: 0 },
  // Front peg only, the back wheel up behind.
  nosegrind: { pivot: "front", pitch: 0.45, yaw: 0, roll: 0, x: 0, y: 0, z: 0 },
  // Back peg only, the front wheel up.
  fiveo: { pivot: "rear", pitch: -0.5, yaw: 0, roll: 0, x: 0, y: 0, z: 0 },
  // The crank on the rail: the bike tipped over toward it, sat down to the bottom bracket.
  boardslide: { pivot: "rear", pitch: 0, yaw: 0, roll: -0.45, x: 0.05, y: -0.06, z: 0 },
  // Back peg, the front wheel turned out over the far side of the rail and dropped (Luc-E).
  lipslide: { pivot: "rear", pitch: 0.12, yaw: 0.5, roll: -0.1, x: 0, y: 0, z: 0 },
  // Back peg, the front wheel tucked in on your side, a little up.
  crooked: { pivot: "rear", pitch: -0.2, yaw: -0.35, roll: 0, x: 0, y: 0, z: 0 },
  // Luc-E's bigger brother: the front wheel right round over the rail and low.
  overcrook: { pivot: "rear", pitch: 0.2, yaw: 0.8, roll: -0.15, x: 0, y: 0, z: 0 },
  // Front peg, the back wheel dropped low on your side.
  smith: { pivot: "front", pitch: -0.35, yaw: 0.25, roll: 0.1, x: 0, y: 0, z: 0 },
  // Back peg, the front wheel over the rail resting on its far side.
  feeble: { pivot: "rear", pitch: 0.18, yaw: 0.3, roll: -0.08, x: 0, y: 0, z: 0 },
};

/**
 * The hands and feet off the bike `apply` knows how to pose. A ride's grab or
 * held trick is posed by name as one of these, or as the ones `BIKE_PARTS`
 * makes it of (`bikePartsOf`); `bike-pose.test.ts` checks every bike trick
 * resolves to at least one, so renaming a trick can't drop its pose.
 */
export const BIKE_POSE_PARTS = ["No-Hander", "X-Up", "Superman", "Seat Grab", "One-Footer", "Can-Can", "No-Footer", "Nac-Nac", "Tabletop"] as const;
export type BikePart = (typeof BIKE_POSE_PARTS)[number];

/**
 * Tricks posed as the parts they're made of (#138, `sim/rides.ts`): the held
 * manual and endo tricks borrow the air grabs' poses (a hand or foot off
 * while up on the one wheel), a Superman Seat Grab is a Superman with the right hand
 * on the seat, and the modified grabs (L1 / R1, `sim/rides.ts`) are two at once.
 */
const BIKE_PARTS: Readonly<Record<string, readonly BikePart[]>> = {
  "Superman Seat Grab": ["Superman", "Seat Grab"],
  Nothing: ["No-Hander", "No-Footer"],
  "X-Up Nac-Nac": ["X-Up", "Nac-Nac"],
  "Hart Attack": ["Superman", "Seat Grab"],
  "One-Foot X-Up": ["One-Footer", "X-Up"],
  "No-Hand Can-Can": ["Can-Can", "No-Hander"],
  "No-Hand Manual": ["No-Hander"],
  "X-Up Manual": ["X-Up"],
  "Can-Can Manual": ["Can-Can"],
  "One-Foot Manual": ["One-Footer"],
  "One-Foot No-Hander": ["One-Footer", "No-Hander"],
  "No-Hand Endo": ["No-Hander"],
  "X-Up Endo": ["X-Up"],
  "Can-Can Endo": ["Can-Can"],
  "One-Foot Endo": ["One-Footer"],
  "Endo Nac-Nac": ["Nac-Nac"],
};

/** Whether a grab or held trick poses the grab `part`: a plain grab is just itself. */
function hasPart(name: string, part: BikePart): boolean {
  return Object.hasOwn(BIKE_PARTS, name) ? BIKE_PARTS[name]!.includes(part) : name === part;
}

/** The parts a grab or held trick is posed as; empty if `apply` doesn't know it (it rides on in the plain pose). */
export function bikePartsOf(name: string): BikePart[] {
  return BIKE_POSE_PARTS.filter((part) => hasPart(name, part));
}

/** Whether a grind style has its own way of hanging the bike on the rail (otherwise it hangs as a Double Peg). */
export function hasPegPose(style: GrindStyle): boolean {
  return Object.hasOwn(PEG_POSES, style);
}

/** The arms out wide, before the hands reach for the grips. */
const ARMS_OUT = { out: 1.5, back: 0.1, bend: 0.2 } as const;
/** Walking the bike (#145): how far out from the rider's middle the hand holds the near grip, how far back the bike sits (m), and its lean in toward them (rad). */
const WALK_HAND_OUT = 0.3;
const WALK_BIKE_Z = -0.3;
const WALK_LEAN_IN = 0.1;
/** An X-Up turns the bars ~100°: any further and the grips cross out of arm's reach. */
const X_UP_TURN = Math.PI * 0.55;

/** How far a turndown has the bars turned, `p` 0..1 of the way: down to ~75° and back. */
function turndownAngle(p: number): number {
  return Math.sin(Math.PI * p) * 1.3;
}

/** The bike moves that spin the bars all the way round (`whipBike`). */
const SPINS_BARS: ReadonlySet<FlipAnim> = new Set(["heelflip", "treflip", "hardflip", "varialheel"]);
/** Where the hand holds the seat (the bike frame's space), and the point a Tabletop rolls the bike and rider about. */
const BIKE_SEAT_GRAB = new THREE.Vector3(0, 0.75, -0.82);
const TABLETOP_AXIS_AT = new THREE.Vector3(0, 0.98, -0.2);
/** How fast (a share a second) the legs straighten out behind for a Superman, and bend back after (see `apply`). */
const BIKE_STRETCH_RATE = 3;
/** Turns of the cranks to one of the back wheel: a BMX's 25/9 gearing. */
const BIKE_GEAR = 25 / 9;
/** The fastest the rider pedals (radians a second, ~80 rpm), and the fastest the cranks come back round to rest. */
const MAX_CADENCE = 8.5;
const CRANK_REST_RATE = 5;
/** Sat on the seat (see `apply`): the hips this far back from over the cranks, this high over the pedals, and the torso leant this much further over the bars. */
const SEAT_BACK = 0.22;
const SEAT_HIP = 0.5;
const SEAT_LEAN = 0.25;

/** The share of a landing's hip dip the rider takes on the bike (see `apply`). */
const BIKE_LAND_DIP = 0.6;
const BIKE_PIVOT = new THREE.Vector3();
const BIKE_BASE = new THREE.Vector3();
const BIKE_Q = new THREE.Quaternion();
const TMP_Q3 = new THREE.Quaternion();
const TMP_Q4 = new THREE.Quaternion();
const X_AXIS = new THREE.Vector3(1, 0, 0);
const Y_AXIS = new THREE.Vector3(0, 1, 0);
const Z_AXIS = new THREE.Vector3(0, 0, 1);
const TMP_V = new THREE.Vector3();
const TMP_V2 = new THREE.Vector3();
